import { SERVICE } from '@ds/swymkit/tools'
import { requirejsOne } from '@ds/swymkit/tools'
import { CSRF } from '@ds/swymkit/tools'
import { cacheDecorator } from '@ds/swymkit/tools'
import type { HTTPError } from 'ky'

import API from '~/services/api'
import { swymFetch } from '~/services/fetch-swym'

import { mediaParser, mediasParser } from '~/entities/content/media/api/parser-media'
import type {
  IDuplicateParams,
  IMedia,
  IMediaResponse,
  TDuplicateResponse,
} from '~/entities/content/media/interfaces/media'
import type { IMediaType } from '~/entities/content/media/interfaces/media-type'

import { getSwymUrl } from '~/features-shared/config-manager/stores/swym-config'
import type { SUBJECT_6W_URI } from '~/features-shared/libs/interfaces/entity'

import { mutualizerDecorator } from '~/features-generic/services/utils/decorator-mutualizer'
import type { ServiceTrackerEventOptions } from '~/features-generic/tracker/interfaces/tracker'

import type { SwymService } from '~/interfaces/crud-service'
import type { SwymResponse } from '~/interfaces/services-dto'

const TIME_OUT_FOR_UPLOAD_MEDIA_REQUESTS = 43200000 // 12 hours

export const service: SwymService<IMedia> = {
  async getAll(params = { page: 1, limit: 15 }) {
    const asAdmin = false

    const response: SwymResponse<IMediaResponse[]> = await swymFetch
      .post(API.Media.Swym.Content, {
        json: {
          params: {
            as_admin: asAdmin,
            ...params,
          },
        },
      })
      .json()

    return {
      total: Number.parseInt(response.nb_result || '0') || 0,
      data: mediasParser(response),
    }
  },
  count() {
    return Promise.resolve({ data: [], total: 0 })
  },
}

export interface IEditMediaOptions {
  is_illustration?: boolean
  subject_uri: SUBJECT_6W_URI
  title?: string
  description?: string
  published?: boolean
  id_media: string
  userFile?: File
  authorizeDownload?: boolean
  thumbnail_time_ms?: number | string
  /** A receipt if the media has been upload from the FCS */
  receipt?: string
}

/** Create an empty media to get the associated FCS upload url and ticket */

export async function editMedia(options: IEditMediaOptions): Promise<SwymResponse<IMedia>> {
  const form = new FormData()

  if (options.title)
    form.append('title', options.title)
  if (options.published !== undefined)
    form.append('published', options.published ? '1' : '0')
  if (options.userFile) {
    form.append(
      'userFile',
      options.userFile,
      options.userFile.name,
    )
  }
  if (options.authorizeDownload !== undefined)
    form.append('download_original_file', options.authorizeDownload ? '1' : '0')
  if (options.thumbnail_time_ms !== undefined)
    form.append('thumbnail_time_ms', options.thumbnail_time_ms.toString())

  form.append('description', options.description || '')
  form.append('is_illustration', options.is_illustration ? 'true' : 'false')
  form.append('community_id', options.subject_uri)
  form.append('id_media', options.id_media)

  const WAFData = await requirejsOne<any>('DS/WAFData/WAFData')
  const response = await new Promise<SwymResponse<IMediaResponse>>((resolve, reject) => {
    WAFData.authenticatedRequest(`${getSwymUrl()}/api/media/editmedia`, {
      responseType: 'json',
      headers: { 'x-ds-swym-csrftoken': CSRF.get(SERVICE.SWYM) },
      type: 'json',
      method: 'POST',
      data: form,
      timeout: TIME_OUT_FOR_UPLOAD_MEDIA_REQUESTS,
      onComplete: resolve,
      onFailure(error: string, response: any) {
        const throwedError = new Error(error) as HTTPError
        throwedError.response = response

        reject(throwedError)
      },
    })
  })

  return await mediaParser(response)
}

export function deleteMedia(id: string, tracker?: ServiceTrackerEventOptions) {
  return swymFetch
    .post('/api/media/deletemedia', {
      json: { params: { id_media: id } },
      tracker,
    })
}

export function getSupportedMedias() {
  return cacheDecorator(
    'getsupportedfileextensionspertype',
    () => swymFetch
      .post('/api/media/getsupportedfileextensionspertype')
      .json<SwymResponse<SupportedMediaExt>>(),
    60000 * 60 * 24,
  )
}

export function restartProcessingMedia(id: string) {
  return swymFetch
    .post('/api/media/restartprocessing', {
      json: {
        params: {
          id_media: id,
        },
      },
    })
}

function _duplicateMedia({
  uri,
  community_id,
  publicationState: publication_state,
}: IDuplicateParams) {
  return swymFetch
    .post(API.Media.Swym.Duplicate, {
      json: {
        params: {
          ids: Array.isArray(uri) ? uri : [uri],
          community_id,
          publication_state,
        },
      },
    })
    .json<TDuplicateResponse>()
}

export const duplicateMedia = mutualizerDecorator<
  typeof _duplicateMedia,
  Parameters<typeof _duplicateMedia>[0]
>({
  fetch: _duplicateMedia,
  merge: (bucketList) => {
    const uris = bucketList.flatMap(el => el.uri)
    const lastItem = bucketList.pop()
    return {
      community_id: lastItem?.community_id as string,
      uri: uris,
      publicationState: lastItem?.publicationState,
    }
  },
  resolve: ([response, [option, resolve]]) =>
    resolve({ ...response, result: { [option.uri as string]: response.result[option.uri as string] } }),
})

export default service

/**
 * Generic type that maps each key of IMediaType to a specified type K
 */
export type MediaTypeConverter<K> = {
  [key in IMediaType]: K;
}

export interface SupportedMediaExt extends MediaTypeConverter<string[]> {
  type2extensions: MediaTypeConverter<string[]>
  maxsize: MediaTypeConverter<number>
  maxpixels: MediaTypeConverter<number>
  always_dl: string[]
  never_dl: string[]
}
